/* ⚠️ GENERATED from app/services/mentions.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   services/mentions.ts — "@" in a group, and who it actually means.

   ══ WHY THIS IS A MODULE AND NOT A REGEX IN THE COMPOSER ══════════════════
   🟥 A MENTION IS FOUR ANSWERS THAT HAVE TO AGREE. The picker decides what you
      are typing at; the composer decides what turns blue while you type; the
      bubble decides what turns blue afterwards, on a different device, days
      later; and the push decides whose phone lights up. Four places, four
      regexes, and the first time they disagree somebody is mentioned in blue
      and never told — or told and never highlighted. So all four read this.

   🟥 AND THE PHONE AND THE BROWSER ARE TWO MORE. This file is twinned into
      allo-web, so a mention typed in Chrome renders the same in the app.

   ══ THE PART THAT LOOKS WRONG AND IS NOT ══════════════════════════════════
   🟢 NO NAMES ARE STORED WITH THE MESSAGE. A message carries `mentions: [uid]`
      and nothing else, and every client re-derives the highlight by looking the
      CURRENT member list up against the text. That is what makes a mention live
      the way the user asked for: change your name and old mentions of you stop
      matching the old spelling — which is honest — while the picker, the
      composer and the notification all follow you immediately.

      The alternative (freezing `{uid, name}` into the document) renders a name
      the person no longer has, forever, to a room that can no longer tell who
      is meant. It also puts a display name on a document the server can read,
      in a product where the server is not supposed to learn what is inside a
      group.

   🟥 THE SERVER LEARNS ONE BIT PER RECIPIENT AND NOTHING ELSE. `mentionedYou`
      rides the per-recipient relay envelope so a mention can pierce a muted
      chat. It is a boolean about a uid the server already routes to. The text,
      the names, and who else was mentioned stay sealed.

   Pure: no react, no react-native, no firebase, no I/O. The suite executes it.
   ═══════════════════════════════════════════════════════════════════════════ */

export const MENTION_TRIGGER = '@';

/* 🟥 "AHMED AL FULAN" IS ONE NAME AND TWO SPACES. A query that dies at the
   first space closes the picker halfway through most Arabic and Spanish names,
   which is the moment the user is still deciding. Three words is enough for
   every name in the member list this has been tried against, and it is short
   enough that an ordinary sentence containing an email or a price does not keep
   a picker open across it. */
export const MENTION_MAX_QUERY_WORDS = 3;

/** How many rows the picker shows at once. */
export const MENTION_PICKER_LIMIT = 8;

/* ══ @everyone ═══════════════════════════════════════════════════
   🟥 THIS IS THE ONE MENTION THAT CANNOT BE UNDONE. A personal mention wakes one
      phone; this wakes the room, and it was decided that it pierces a mute the same
      way — so it is the loudest thing an ordinary member can do in this app. That is
      why it is gated by group size rather than left open: in a family-sized room it
      is a convenience and a mistake costs nothing, and in a two-hundred-person room
      it is a broadcast and a mistake costs two hundred interruptions.

   🟥 ONE SPELLING, ON PURPOSE. "@all" as a second token doubles the picker row,
      doubles the segmenter's work, and gives a group two ways to say one thing that
      have to stay in step forever. Anyone typing "@all" sees "everyone" in the
      picker on the first letter and picks it — which is how the feature is meant to
      be used anyway.

   🟥 AND THE SENTINEL IS NEVER STORED AS A UID. `mentions` on a message stays a
      list of real people; a room-wide mention rides as its own boolean, so nothing
      downstream can mistake it for a member and try to look up its name. */
export const MENTION_EVERYONE = '@everyone';
export const MENTION_EVERYONE_NAME = 'everyone';

/* 🟢 TEN, because that is roughly where a group stops being a conversation and
   starts being an audience. It is a constant rather than a magic number so the line
   can be moved once when somebody disagrees with it. */
export const MENTION_EVERYONE_ADMIN_ABOVE = 10;

/**
 * May this person mention the whole room?
 *
 * 🟥 AN UNKNOWN SIZE IS TREATED AS BIG. A member list that has not loaded is not
 *    evidence of a small group, and the failure directions are not symmetrical:
 *    wrongly hiding the row costs somebody one retry, wrongly showing it costs
 *    everybody an interruption they did not agree to.
 */
export function canMentionEveryone(opts) {
  if (opts.isAdmin) return true;
  const n = Number(opts.memberCount);
  if (!Number.isFinite(n) || n <= 0) return false;
  return n < MENTION_EVERYONE_ADMIN_ABOVE;
}

/**
 * The member list a picker or a segmenter should work from.
 *
 * 🟢 THE ROW GOES FIRST so an empty "@" offers it before scrolling, and so the
 *    segmenter meets it before any person whose name happens to start the same way.
 */
export function withEveryone(members, allowed) {
  const list = Array.isArray(members) ? members : [];
  if (!allowed) return list;
  return [{
    uid: MENTION_EVERYONE,
    name: MENTION_EVERYONE_NAME
  }, ...list];
}

/** Does this text mention the whole room, given who was allowed to? */
export function mentionsEveryone(text, members) {
  return segmentMentions(text, members).some(s => s.uid === MENTION_EVERYONE);
}

/* ── normalisation ──────────────────────────────────────────────────────────
   🟥 "José" TYPED AS "Jose" FOUND NOBODY, and neither did "محمّد" typed without
      the shadda. Latin accents decompose under NFD and the marks fall in the
      combining block; Arabic tashkeel does not decompose and has to be named.
      Both are invisible to the person typing, so both are stripped on each
      side of every comparison. */
const _COMBINING = /[̀-ͯ]/g;
const _TASHKEEL = /[ً-ْٰـ]/g;
export function normalizeForMention(s) {
  return String(s == null ? '' : s).normalize('NFD').replace(_COMBINING, '').replace(_TASHKEEL, '').toLowerCase().trim();
}

/** Collapse the whitespace a pasted name can carry; never returns a newline. */
export function cleanMentionName(s) {
  return String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
}

/**
 * 🟥 THE USER ASKED FOR NAMES, NOT USERNAMES, AND THIS IS WHERE THAT IS TRUE.
 *    A member row can arrive from three places — the live user document, the
 *    chat's `participantNames` map, or a stale navigation param — and only the
 *    first two ever hold a real name. A username is the last resort before
 *    showing a raw uid, which is not a person.
 */
export function mentionNameOf(uid, live, participantNames) {
  const fromLive = cleanMentionName(live?.fullName);
  if (fromLive) return fromLive;
  const fromChat = cleanMentionName(participantNames?.[uid]);
  if (fromChat) return fromChat;
  const uname = cleanMentionName(live?.username);
  if (uname) return uname;
  return '';
}

/* ── what you are typing at ─────────────────────────────────────────────────*/

const _isBoundary = ch => ch === undefined || /\s/.test(ch);

/**
 * The `@…` run the caret is currently inside, or null.
 *
 * 🟥 `a@b.com` IS NOT A MENTION. The trigger has to open a word — start of the
 *    text or after whitespace — or every email address in the chat opens a
 *    picker mid-sentence.
 */
export function activeMentionQuery(text, caret) {
  const s = String(text == null ? '' : text);
  let pos = Number(caret);
  if (!Number.isFinite(pos)) return null;
  pos = Math.max(0, Math.min(Math.floor(pos), s.length));
  for (let i = pos - 1; i >= 0; i -= 1) {
    const ch = s[i];
    if (ch === '\n') return null; // a mention does not span lines
    if (ch === MENTION_TRIGGER) {
      if (!_isBoundary(s[i - 1])) return null;
      const query = s.slice(i + 1, pos);
      /* 🟥 THERE WAS A `query.indexOf('@') >= 0` GUARD HERE AND IT WAS DEAD CODE.
         A mutation run deleted it and nothing failed, which is the only reason
         anybody looked: this scan walks BACKWARDS, so any '@' inside the run is
         reached first and has already either returned null (it did not open a
         word) or become the trigger itself (it did). The query can never contain
         one. Reasonable-looking, tested-looking, and never once executed. */
      /* 🟢 the word budget is counted on the QUERY, so "@" alone (zero words)
         is a valid, deliberately empty query — that is the tap that opens the
         whole member list, which is how most people pick. */
      if (query.trim() && query.trim().split(/\s+/).length > MENTION_MAX_QUERY_WORDS) return null;
      if (/\s{2,}/.test(query)) return null; // a double space ends the attempt
      return {
        start: i,
        query
      };
    }
  }
  return null;
}

/* ── who that is ────────────────────────────────────────────────────────────*/

/**
 * Members matching a query, best first.
 *
 * 🟢 RANKED, NOT FILTERED. Typing "al" in a room with an Ali and a Faisal Al
 *    Harbi should put Ali first and still offer Faisal — a hard "starts with"
 *    filter hides the person you meant half the time, and a plain `includes`
 *    buries them under whoever happens to be earlier in the list.
 */
export function filterMentionMembers(members, query, limit = MENTION_PICKER_LIMIT) {
  const list = Array.isArray(members) ? members.filter(m => m && m.uid && cleanMentionName(m.name)) : [];
  const q = normalizeForMention(query);
  if (!q) return list.slice(0, limit);
  const scored = [];
  list.forEach((m, i) => {
    const name = normalizeForMention(m.name);
    const user = normalizeForMention(m.username);
    let score = -1;
    if (name.startsWith(q)) score = 0;else if (name.split(' ').some(w => w.startsWith(q))) score = 1;else if (name.indexOf(q) >= 0) score = 2;else if (user && user.startsWith(q)) score = 3;else if (user && user.indexOf(q) >= 0) score = 4;
    if (score >= 0) scored.push({
      m,
      score,
      i
    });
  });
  /* 🟢 the index tiebreak keeps the order the caller chose (the group's own
     member order) stable — a picker whose rows reshuffle between keystrokes at
     equal score is a picker you tap the wrong row in. */
  scored.sort((a, b) => a.score - b.score || a.i - b.i);
  return scored.slice(0, limit).map(x => x.m);
}

/* ── putting one in ─────────────────────────────────────────────────────────*/

/**
 * Replace the live `@…` run with a finished mention.
 *
 * 🟢 IT ENDS IN A SPACE, and that space is what closes the picker: the next
 *    `activeMentionQuery` reads the run as ended. Without it the picker stays
 *    open over the name it just inserted, and the second tap picks a second
 *    person into the middle of the first one's name.
 */
export function applyMention(text, start, caret, name) {
  const s = String(text == null ? '' : text);
  const a = Math.max(0, Math.min(Math.floor(Number(start)) || 0, s.length));
  let b = Math.floor(Number(caret));
  if (!Number.isFinite(b)) b = a;
  b = Math.max(a, Math.min(b, s.length));
  const clean = cleanMentionName(name);
  if (!clean) return {
    text: s,
    caret: b
  };
  const token = `${MENTION_TRIGGER}${clean}`;
  /* 🟢 don't stack spaces — inserting before an existing one is the common case
     when somebody goes back to fix a name they already typed past. */
  const needsSpace = s[b] !== ' ';
  const inserted = needsSpace ? `${token} ` : token;
  return {
    text: s.slice(0, a) + inserted + s.slice(b),
    caret: a + token.length + 1
  };
}

/* ── reading one back out ───────────────────────────────────────────────────*/

const _escape = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* 🟥 A MENTION ENDS WHERE THE NAME ENDS, NOT WHERE THE WORD DOES. "@Ali," and
   "@Ali!" and "@Ali" at the end of a line are all Ali; "@Alison" is not. So the
   character after the match must not be one a name could continue with. */
const _canContinueName = ch => ch !== undefined && /[\p{L}\p{N}_]/u.test(ch);

/**
 * The message, split into plain runs and mention runs.
 *
 * 🟥 LONGEST NAME FIRST, AND CONSUMED. In a room with "Ali" and "Ali Hassan",
 *    the text "@Ali Hassan" matches BOTH — and a naive scan reports a mention of
 *    Ali followed by the stray word "Hassan", which highlights the wrong half
 *    and notifies the wrong person. Sorting by name length and consuming the
 *    match is the whole fix, and it is why segmenting and collecting share one
 *    function instead of being two regexes that almost agree.
 */
export function segmentMentions(text, members) {
  const s = String(text == null ? '' : text);
  if (!s) return [];
  const list = (Array.isArray(members) ? members : []).filter(m => m && m.uid && cleanMentionName(m.name)).map(m => ({
    uid: m.uid,
    name: cleanMentionName(m.name)
  })).sort((a, b) => b.name.length - a.name.length);
  if (!list.length) return [{
    text: s
  }];
  const out = [];
  let plain = '';
  let i = 0;
  while (i < s.length) {
    let hit = null;
    if (s[i] === MENTION_TRIGGER && _isBoundary(s[i - 1])) {
      for (const m of list) {
        const end = i + 1 + m.name.length;
        if (s.slice(i + 1, end).toLowerCase() !== m.name.toLowerCase()) continue;
        if (_canContinueName(s[end])) continue;
        hit = {
          uid: m.uid,
          len: 1 + m.name.length
        };
        break;
      }
    }
    if (hit) {
      if (plain) {
        out.push({
          text: plain
        });
        plain = '';
      }
      out.push({
        text: s.slice(i, i + hit.len),
        uid: hit.uid
      });
      i += hit.len;
    } else {
      plain += s[i];
      i += 1;
    }
  }
  if (plain) out.push({
    text: plain
  });
  return out;
}

/**
 * Who this message actually mentions, right now.
 *
 * 🟥 DERIVED FROM THE TEXT, NOT FROM WHAT THE PICKER RECORDED. Pick a name, then
 *    backspace over it, then send: a list kept by the picker still holds that
 *    uid, and somebody gets "you were mentioned" for a word that is not in the
 *    message. The text is the message; anything else is a guess about it.
 */
export function collectMentions(text, members) {
  const seen = new Set();
  const out = [];
  for (const seg of segmentMentions(text, members)) {
    /* 🟥 THE ROOM IS NOT A PERSON. `@everyone` segments like any other mention so
       it turns blue with the rest, but letting its sentinel into this list would put
       a fake uid on the message document — where the bubble would open a listener
       for it, the relay would look for a socket for it, and nothing would say why. */
    if (!seg.uid || seg.uid === MENTION_EVERYONE || seen.has(seg.uid)) continue;
    seen.add(seg.uid);
    out.push(seg.uid);
  }
  return out;
}

/** True when this recipient should be told, mute or no mute. */
export function isMentioned(uid, mentions) {
  const u = String(uid == null ? '' : uid);
  if (!u) return false;
  return Array.isArray(mentions) && mentions.some(m => String(m) === u);
}

/* ── whether the picker is open at all ──────────────────────────────*/

/**
 * What the picker should be showing, or null for "closed".
 *
 * 🟥 THE TRAILING SPACE IS NOT ENOUGH ON ITS OWN, and the first draft of this
 *    module believed it was. `applyMention` ends a mention with a space so the
 *    run reads as finished — but "@Ali " is still a perfectly good QUERY for Ali,
 *    so the picker re-opened over the name it had just inserted and the next tap
 *    landed inside the first name.
 *
 * 🟥 AND THE OBVIOUS FIX IS WORSE: closing whenever the query ends in a space
 *    closes it halfway through "@Nora Al Budragh", which is the exact case the
 *    three-word budget exists for.
 *
 * 🟢 SO IT ASKS THE SEGMENTER, which is the same function the bubble uses.
 *    The picker closes when the run already resolves to a WHOLE member and there
 *    is nothing but whitespace between that name and the caret. "@Ali " closes;
 *    "@Ali H" does not, because the gap is not whitespace and the user is
 *    plainly still typing "Ali Hassan"; "@Ali" with the caret against it does
 *    not either, for the same reason.
 *
 * 🟢 AND NO MATCHES CLOSES IT TOO. A picker with no rows is a strip of empty
 *    chrome over the keyboard while somebody types an email address.
 */
export function mentionPickerState(text, caret, members, limit = MENTION_PICKER_LIMIT) {
  const active = activeMentionQuery(text, caret);
  if (!active) return null;
  const s = String(text == null ? '' : text);
  let pos = Number(caret);
  if (!Number.isFinite(pos)) return null;
  pos = Math.max(0, Math.min(Math.floor(pos), s.length));
  let at = 0;
  for (const seg of segmentMentions(s, members)) {
    if (at === active.start && seg.uid) {
      const gap = s.slice(at + seg.text.length, pos);
      if (gap.length > 0 && !/\S/.test(gap)) return null; // finished — stand down
    }
    at += seg.text.length;
  }
  const results = filterMentionMembers(members, active.query, limit);
  if (!results.length) return null;
  return {
    start: active.start,
    query: active.query,
    results
  };
}

/* ── the notification ───────────────────────────────────────────────────────*/

/**
 * 🟥 A MENTION PIERCES A MUTED CHAT, AND NOTHING ELSE DOES. That is the whole
 *    point of muting a busy group: the room is quiet until somebody means YOU.
 *    A chat you have LEFT is still silent — leaving is not muting — and so is a
 *    person who turned message notifications off account-wide, because that is
 *    a setting about their phone rather than about this room.
 */
export function mentionPiercesMute(opts) {
  if (opts.isLeft) return false;
  if (opts.notificationsEnabled === false) return false;
  if (!opts.isMuted) return true;
  return !!opts.mentioned;
}

/** Body for "Allo mentioned you in School". The title is already the group. */
export function mentionPushBody(actorName, groupName) {
  const who = cleanMentionName(actorName) || 'Someone';
  const room = cleanMentionName(groupName);
  return room ? `${who} mentioned you in ${room}` : `${who} mentioned you`;
}
export const MENTION_PUSH_BODY_KEY = 'mention_push_body';
